<template>
  <div class="daily-foc">
    <v-tooltip
      :bottom="position === 'bottom'"
      :top="position === 'top'"
      :left="position === 'left'"
      :right="position === 'right'"
      :offset-x="offsetX"
      :offset-y="offsetY"
    >
      <template v-slot:activator="{ on, attrs }">
        <span
          v-bind="attrs"
          class="foc-total"
          style="cursor:pointer"
          v-on="on"
        >
          {{ displayTotal }}
        </span>
      </template>

      <v-card class="foc-card pa-3">
        <div class="foc-header">
          {{ dataType === 'fuelTypes' ? 'Fuel Breakdown' : 'Engine Consumption' }}
        </div>

        <!-- Fuel Type Structure -->
        <div
          v-if="dataType === 'fuelTypes'"
          class="foc-body"
        >
          <div
            v-for="fuel in availableFuelTypes"
            :key="fuel"
            class="foc-row"
          >
            <div class="fuel-name">
              {{ fuelLabel(fuel) }}:
            </div>
            <div class="fuel-values">
              <span>ME: {{ formatFuel(focData[fuel] ? focData[fuel].me : 0) }}</span>
              <span>AUX: {{ formatFuel(focData[fuel] ? focData[fuel].aux : 0) }}</span>
              <span>BOIL: {{ formatFuel(focData[fuel] ? focData[fuel].boil : 0) }}</span>
            </div>
          </div>
        </div>

        <!-- Engine Structure -->
        <div
          v-else-if="dataType === 'engines'"
          class="foc-body"
        >
          <div class="foc-row">
            <div class="fuel-name">
              MAIN ENGINE:
            </div>
            <div class="fuel-value">
              {{ formatFuel(focData.me) }}
            </div>
          </div>
          <div class="foc-row">
            <div class="fuel-name">
              AUX ENGINE:
            </div>
            <div class="fuel-value">
              {{ formatFuel(focData.aux) }}
            </div>
          </div>
          <div class="foc-row">
            <div class="fuel-name">
              BOILER:
            </div>
            <div class="fuel-value">
              {{ formatFuel(focData.boil) }}
            </div>
          </div>
          <div
            v-if="isCompany5"
            class="foc-row"
          >
            <div class="fuel-name">
              COMP. BOILER:
            </div>
            <div class="fuel-value">
              {{ formatFuel(focData.compBoiler) }}
            </div>
          </div>
          <div
            v-if="focData.day"
            class="foc-row total-row"
          >
            <div class="fuel-name">
              DATE:
            </div>
            <div class="fuel-value">
              {{ formatDate(focData.day) }}
            </div>
          </div>
        </div>
        <!-- Unknown Structure -->
        <div
          v-else
          class="foc-body"
        >
          <div class="no-data">
            No valid fuel data available
          </div>
        </div>
      </v-card>
    </v-tooltip>
  </div>
</template>

<script>
const FUEL_KEYS = ["hfo", "lfo", "mgo"];

import { onConfig } from "@/config";

let config = null;
onConfig((c) => {
  config = c;
});

export default {
  name: "FocHover",
  props: {
    focData: {
      type: Object,
      required: true
    },
    position: {
      type: String,
      default: "bottom"
    },
    offsetX: {
      type: Number,
      default: 0
    },
    offsetY: {
      type: Number,
      default: 0
    }
  },
  computed: {
    isCompany5() {
      return config.VUE_APP_COMPANY_ID === "5";
    },

    availableFuelTypes() {
      if (!this.focData || typeof this.focData !== "object") return [];
      return FUEL_KEYS.filter((fuel) => this.focData[fuel] !== undefined);
    },

    dataType() {
      if (!this.focData) return "unknown";

      if (this.availableFuelTypes.length > 0) {
        return "fuelTypes";
      }

      if (
        this.focData.me !== undefined ||
        this.focData.aux !== undefined ||
        this.focData.boil !== undefined ||
        this.focData.compBoiler !== undefined

      ) {
        return "engines";
      }

      return "unknown";
    },

    displayTotal() {
      const total = this.getTotal(this.focData);
      return `${total} [tn/day]`;
    }
  },
  methods: {
    fuelLabel(fuel) {
      if (fuel === "hfo") return this.isCompany5 ? "HSFO" : "HFO";
      if (fuel === "lfo") return this.isCompany5 ? "VLSFO" : "LFO";
      return fuel.toUpperCase();
    },

    normalizeValue(value) {
      const num = parseFloat(value);
      if (isNaN(num)) return 0;
      return num > 150 ? num / 100 : num;
    },

    formatFuel(value) {
      const normalized = this.normalizeValue(value);
      return normalized.toFixed(2);
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "Invalid date";

        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric"
        });
      } catch {
        return "Invalid date";
      }
    },

    getTotal(fuelObj) {
      if (!fuelObj) return "0.00";

      if (this.dataType === "fuelTypes") {
        const total = this.availableFuelTypes.reduce((acc, fuelType) => {
          const fData = fuelObj[fuelType];

          return acc + (
            fData
              ? parseFloat(fData.me || 0) +
              parseFloat(fData.aux || 0) +
              parseFloat(fData.boil || 0)
              : 0
          );
        }, 0);

        return this.formatFuel(total);
      }

      if (this.dataType === "engines") {
        if (fuelObj.total !== undefined) {
          return this.formatFuel(fuelObj.total);
        }

        return this.formatFuel(
          parseFloat(fuelObj.me || 0) +
          parseFloat(fuelObj.aux || 0) +
          parseFloat(fuelObj.boil || 0) +
          (this.isCompany5 ? parseFloat(fuelObj.compBoiler || 0) : 0)
        );
      }

      return "0.00";
    }
  }
};
</script>

<style scoped>
.foc-card {
  border-radius: 16px;
  background: linear-gradient(135deg, #1e344f, #1e344f);
  color: #f0f4f8;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-width: 220px;
}

.foc-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
}

.foc-header {
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 10px;
  border-bottom: 1px solid #2a4365;
  padding-bottom: 6px;
  color: #ffffff;
}

.foc-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.foc-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  padding: 6px 10px;
  border-radius: 8px;
  transition: background 0.2s;
}

.foc-row:hover {
  background: rgba(255, 255, 255, 0.1);
}

.total-row {
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.fuel-name {
  font-weight: 600;
  color: #ffc107;
  font-size: 13px;
}

.fuel-values {
  display: flex;
  gap: 8px;
}

.fuel-values span {
  font-size: 12px;
  color: #f0f4f8;
}

.fuel-value {
  font-size: 13px;
  color: #ffffff;
  font-weight: 500;
}

.no-data {
  text-align: center;
  color: #9e9e9e;
  font-style: italic;
  padding: 10px 0;
}
</style>
